import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import moment from 'moment-timezone'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import { getTimezoneOffsetMinutes } from '../../utils/timezone'
import ScheduleDatePicker from './ScheduleDatePicker'
import ScheduleTimeSlots from './ScheduleTimeSlots'
import ScheduleSettingsModal from './ScheduleSettingsModal'
import SelectedDatesList from './SelectedDatesList'
import SchedulingHeader from './components/SchedulingHeader'
import SchedulingMetaPanel from './components/SchedulingMetaPanel'
import {
  getUnavailableSlotsTooltip,
  hasBlockingUnavailableSlots,
  getSchedulableDatesFromList,
  toggleDateIncludedInList,
  isWeeklyWarning,
  isHardSchedulingBlock,
  isSessionRestrictionSlot,
  FIRST_SESSION_REQUIRED_MESSAGE,
  isFirstSessionScheduling,
  isFirstSessionMissing,
} from '@abroad/rest-client'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import useSchedulingRestrictions from '../../hooks/useSchedulingRestrictions'
import { useRecurringPatterns } from './hooks/useRecurringPatterns'
import { useSlotAvailability } from './hooks/useSlotAvailability'
import { useScheduleSubmission } from './hooks/useScheduleSubmission'
import { useDateSlotConversion } from './hooks/useDateSlotConversion'

/**
 * Reusable Session Scheduling Calendar Component for Admin
 * Handles multiple scheduling flows:
 * - First session scheduling
 * - Remaining sessions scheduling
 * - Schedule Multiple (multiple selection)
 */
const SessionSchedulingCalendar = ({
  schedulingType = 'first-session', // 'first-session' | 'remaining' | 'schedule-all'
  coachDetails = null,
  onScheduleSuccess = null, // Callback after successful scheduling
  onBack = null, // Callback to go back
  timezone, // Timezone from parent (required)
}) => {
  const Error = useErrorService()
  const Notification = useNotificationService()
  //   const { user } = useUserService()

  const coachClientName = useMemo(() => {
    const client =
      coachDetails?.clientName || coachDetails?.client?.fullName || '-'
    const coach = coachDetails?.coachName || '-'
    return `${coach} & ${client}`
  }, [coachDetails])
  const coachId = coachDetails?.coachId
  const scheduleConfig = coachDetails?.scheduleConfig
  const hasGrowthInRemaining = scheduleConfig?.hasGrowthInRemaining
  const firstSlotDuration = scheduleConfig?.firstSlotDuration
  const otherSlotsDuration = scheduleConfig?.otherSlotsDuration
  const sowType = coachDetails?.sowType || coachDetails?.sow?.sowType
  const sowSessionDuration =
    schedulingType === 'first-session' && hasGrowthInRemaining
      ? firstSlotDuration
      : otherSlotsDuration
  // Growth is the first date in a schedule-all list only while it is still remaining
  const hasGrowthFirstInSchedule =
    schedulingType === 'schedule-all' &&
    hasGrowthInRemaining &&
    firstSlotDuration
  const firstDateDuration = hasGrowthFirstInSchedule
    ? firstSlotDuration
    : sowSessionDuration
  const sowId = coachDetails?.sowId

  const [selectedDate, setSelectedDate] = useState(
    moment().format('YYYY-MM-DD'),
  )
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [availableDays, setAvailableDays] = useState({})
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'))
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const preferredDateRef = useRef(selectedDate)
  const [timeFormat] = useState('12h')

  const [showScheduleSettingsModal, setShowScheduleSettingsModal] =
    useState(false)
  const [selectedDatesList, setSelectedDatesList] = useState([])
  const [editingDateIndex, setEditingDateIndex] = useState(null)
  const [editingBaseDate, setEditingBaseDate] = useState(null)
  const [showSelectedDates, setShowSelectedDates] = useState(false)
  const [editingduration, setEditingduration] = useState(null)
  const skipNextLoadRef = useRef(false)
  const editingDateIndexRef = useRef(null)

  const timezoneOffsetMinutes = useMemo(() => {
    if (!timezone) {
      return null
    }
    return getTimezoneOffsetMinutes(timezone)
  }, [timezone])

  const { convertDateAndSlotToUTC } = useDateSlotConversion(timezone)

  const {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  } = useSchedulingRestrictions({
    onSuccess: (data) => {
      if (onScheduleSuccess) {
        if (schedulingType === 'schedule-all') {
          onScheduleSuccess(data?.scheduledSessions)
        } else {
          onScheduleSuccess(data?.event)
        }
      }
    },
    onSameDayConflict: (message) => {
      Notification.showNotification(message, 'danger')
    },
    onError: (message) => {
      Notification.showNotification(message, 'danger')
    },
  })

  const remainingSessions = coachDetails?.remainingSessions
  const getTotalSessions = useCallback(() => {
    if (schedulingType === 'first-session') {
      return 1
    }
    if (schedulingType === 'remaining' && remainingSessions) {
      return remainingSessions
    }
    if (schedulingType === 'schedule-all' && remainingSessions) {
      return remainingSessions
    }
    return 1
  }, [schedulingType, remainingSessions])

  const loadMonthSlots = useCallback(
    async (targetMonth, preferredDate) => {
      if (!coachId || !targetMonth) {
        return
      }

      try {
        setIsLoadingSlots(true)
        const normalizedMonth = moment(targetMonth).startOf('month')

        if (!normalizedMonth.isValid()) {
          setIsLoadingSlots(false)
          return
        }

        const monthNumber = normalizedMonth.month() + 1
        const yearNumber = normalizedMonth.year()

        const payload = {
          month: Number(monthNumber),
          year: Number(yearNumber),
        }

        const totalSessions = getTotalSessions()
        if (
          schedulingType === 'schedule-all' &&
          editingDateIndex !== null &&
          editingDateIndex >= 0 &&
          totalSessions <= 3
        ) {
          payload.sessionIndex = editingDateIndex + 1
        }

        const durationToUse = editingduration || firstDateDuration
        if (durationToUse) {
          payload.sowSessionDuration = durationToUse
        }
        if (sowId) {
          payload.sowId = sowId
        }
        if (timezone) {
          payload.clientTimezone = timezone
        }

        const { data } = await API.ClientAPI.session.getAvailableSlots(
          coachId,
          payload,
        )
        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        setAvailableDays(daysMap)

        const nextSelectableDate = (() => {
          const isEditingSlot = editingDateIndexRef.current !== null

          // When editing a list item, keep the clicked date even if it has no
          // coach slots (e.g. same-day conflict) — do not jump to the 1st of month.
          if (
            schedulingType === 'schedule-all' &&
            isEditingSlot &&
            preferredDate
          ) {
            return preferredDate
          }

          if (preferredDate && daysMap[preferredDate]?.slots?.length) {
            return preferredDate
          }

          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          return firstDayWithSlots?.date || null
        })()

        setSelectedDate(nextSelectableDate)
        setSelectedSlot(null)
      } catch (error) {
        Error.showError(error)
        setAvailableDays({})
        setSelectedDate(null)
        setSelectedSlot(null)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coachId,
      firstDateDuration,
      schedulingType,
      editingDateIndex,
      timezone,
      getTotalSessions,
      editingduration,
      sowId,
    ],
  )

  useEffect(() => {
    preferredDateRef.current = selectedDate
  }, [selectedDate])

  useEffect(() => {
    if (!coachId || !currentMonth) {
      return
    }

    if (skipNextLoadRef.current && editingDateIndex === null) {
      skipNextLoadRef.current = false
      return
    }

    loadMonthSlots(currentMonth, preferredDateRef.current)
  }, [
    coachId,
    currentMonth,
    loadMonthSlots,
    schedulingType,
    showSelectedDates,
    editingDateIndex,
    editingduration,
  ])

  useEffect(() => {
    if (editingDateIndex !== null) {
      if (selectedDate && availableDays[selectedDate]?.slots?.length) {
        setTimeSlots(availableDays[selectedDate].slots)
      } else {
        setTimeSlots([])
      }
      return
    }

    if (
      (!selectedDate || !availableDays[selectedDate]?.slots?.length) &&
      Object.keys(availableDays).length > 0
    ) {
      const firstDayWithSlots = Object.values(availableDays).find(
        (day) => day?.slots?.length,
      )
      if (firstDayWithSlots?.date) {
        setSelectedDate(firstDayWithSlots.date)
        return
      }
    }

    if (selectedDate && availableDays[selectedDate]?.slots?.length) {
      setTimeSlots(availableDays[selectedDate].slots)
      return
    }

    setTimeSlots([])
  }, [selectedDate, availableDays, editingDateIndex])

  const handleDateSelect = (date) => {
    if (schedulingType === 'schedule-all' && editingDateIndex !== null) {
      const editingDateItem = selectedDatesList[editingDateIndex]
      const baseDate = editingBaseDate || editingDateItem?.date

      if (!editingDateItem || !baseDate) {
        return
      }

      const isEditingSessionRestriction =
        isSessionRestrictionSlot(editingDateItem)

      if (date) {
        const updatedDates = [...selectedDatesList]

        updatedDates[editingDateIndex] = {
          ...editingDateItem,
          date,
          slotStart: null,
          slotEnd: null,
          isSlotAvailable: true,
          isIncluded: true,
          ...(isEditingSessionRestriction
            ? {
                sessionConstraint: null,
                isSameDayConflict: false,
                isWeeklyLimitWarning: false,
                isMaxSessionsPerWeekExceeded: false,
                slotRestrictionMessage: null,
              }
            : {}),
        }

        setSelectedDatesList(updatedDates)
        setSelectedDate(date)
        setSelectedSlot(null)

        const dateMoment = moment(date)
        if (!dateMoment.isValid()) {
          return
        }
        const monthStart = dateMoment.clone().startOf('month')
        preferredDateRef.current = date

        setCurrentMonth((prev) => {
          if (prev && prev.isSame(monthStart, 'month')) {
            return prev
          }
          return monthStart
        })
      }

      return
    }

    if (
      schedulingType === 'schedule-all' &&
      showSelectedDates &&
      editingDateIndex === null &&
      date
    ) {
      handleResetDates()
    }
    setSelectedDate(date)
    setSelectedSlot(null)
  }

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    setCurrentMonth((prev) => {
      if (prev && prev.isSame(normalizedMonth, 'month')) {
        return prev
      }

      return normalizedMonth
    })
  }, [])

  const handleSlotClickInternal = (slot) => {
    handleSlotSelect(slot)
  }

  const handleSlotSelect = async (slot) => {
    setSelectedSlot(slot)

    if (
      schedulingType === 'schedule-all' &&
      editingDateIndex !== null &&
      slot
    ) {
      const updatedDates = [...selectedDatesList]
      const editingDate = updatedDates[editingDateIndex]
      const originalDate = editingDate?.date

      if (!originalDate) {
        return
      }

      const isEditingSessionRestriction = isSessionRestrictionSlot(editingDate)
      const dateForSlot =
        isEditingSessionRestriction && selectedDate
          ? selectedDate
          : originalDate

      const durationMinutes = Number(editingduration || sowSessionDuration) || 0

      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(dateForSlot, slot.start, slot.end)

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      updatedDates[editingDateIndex] = {
        ...editingDate,
        date: dateForSlot,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
        isSlotAvailable: true,
        isIncluded: true,
        sessionConstraint: null,
        isSameDayConflict: false,
        isWeeklyLimitWarning: false,
        isMaxSessionsPerWeekExceeded: false,
        slotRestrictionMessage: null,
      }

      setSelectedDatesList(updatedDates)
      editingDateIndexRef.current = null
      setEditingDateIndex(null)
      setEditingduration(null)
      skipNextLoadRef.current = true
      try {
        const mergedDates = await checkSlotAvailabilityForDates(updatedDates)
        setSelectedDatesList(mergedDates)
      } catch (error) {
        Error.showError(error)
      }
      return
    }

    if (schedulingType === 'schedule-all' && slot && selectedDate) {
      const totalSessions = getTotalSessions()

      if (totalSessions === 1) {
        const durationMinutes = Number(firstDateDuration) || 0

        // Convert selected date + slot time to UTC (accounting for user's timezone)
        const { start: slotStart, end: slotEndFromConversion } =
          convertDateAndSlotToUTC(selectedDate, slot.start, slot.end)

        const slotEnd = durationMinutes
          ? slotStart.clone().add(durationMinutes, 'minutes')
          : slotEndFromConversion || slotStart.clone()

        const singleDate = {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
          isSlotAvailable: true,
        }

        setSelectedDatesList([singleDate])
        setShowSelectedDates(true)
        handleCheckSlotAvailability([singleDate])
      } else {
        setShowScheduleSettingsModal(true)
      }
    }
  }

  const { generateRecurringDates } = useRecurringPatterns(
    convertDateAndSlotToUTC,
    sowSessionDuration,
  )

  const { checkSlotAvailabilityForDates, isCheckingAvailability } =
    useSlotAvailability(coachId, firstDateDuration, sowId)

  const { handleSchedule, isScheduling } = useScheduleSubmission(
    schedulingType,
    sowId,
    sowSessionDuration,
    timezone,
    convertDateAndSlotToUTC,
    onScheduleSuccess,
    runScheduling,
    coachDetails?.isConditionalSchedulingEnabled === true,
  )

  const handleCheckSlotAvailability = useCallback(
    async (dates) => {
      try {
        const updatedDates = await checkSlotAvailabilityForDates(dates)
        setSelectedDatesList(updatedDates)
      } catch (error) {
        Error.showError(error)
      }
    },
    [checkSlotAvailabilityForDates, Error],
  )

  const handleRecurringPatternConfirm = useCallback(
    async (pattern) => {
      if (!selectedDate || !selectedSlot || !pattern) {
        return
      }

      const totalSessions = getTotalSessions()
      const generatedDates = generateRecurringDates(
        selectedDate,
        pattern,
        totalSessions,
        selectedSlot,
      )

      setSelectedDatesList(generatedDates)
      setShowSelectedDates(true)
      await handleCheckSlotAvailability(generatedDates)
    },
    [
      selectedDate,
      selectedSlot,
      generateRecurringDates,
      getTotalSessions,
      handleCheckSlotAvailability,
    ],
  )

  const handleScheduleWithErrorHandling = useCallback(async () => {
    try {
      let result
      if (schedulingType === 'schedule-all') {
        if (!selectedDatesList || selectedDatesList.length === 0) {
          Error.showError({ message: 'Please select dates for scheduling' })
          return
        }
        // First session is compulsory while nothing is booked yet
        if (
          isFirstSessionScheduling(coachDetails) &&
          isFirstSessionMissing(selectedDatesList)
        ) {
          Notification.showNotification(
            FIRST_SESSION_REQUIRED_MESSAGE,
            'danger',
          )
          return
        }
        result = await handleSchedule(null, null, selectedDatesList)
      } else {
        if (!selectedDate || !selectedSlot) {
          Error.showError({ message: 'Please select a date and time slot' })
          return
        }
        result = await handleSchedule(selectedDate, selectedSlot, null)
      }

      if (result?.success) {
        Notification.showNotification(result.message, 'success')
      }
    } catch (error) {
      if (isWeeklyWarning(error) || isHardSchedulingBlock(error)) {
        return
      }
      if (error.message) {
        Notification.showNotification(error.message, 'danger')
      } else {
        Error.showError(error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schedulingType,
    selectedDate,
    selectedSlot,
    selectedDatesList,
    coachDetails,
    handleSchedule,
    Notification,
  ])

  const handleDateClickFromList = (dateItem, index) => {
    if (dateItem.isSlotAvailable === false) {
      editingDateIndexRef.current = index
      preferredDateRef.current = dateItem.date
      setEditingDateIndex(index)
      setEditingBaseDate(dateItem.date || null)
      setEditingduration(dateItem.duration || null)
      skipNextLoadRef.current = false
      setSelectedDate(dateItem.date)
      setSelectedSlot(null)
      const dateMoment = moment(dateItem.date)
      if (!dateMoment.isValid()) {
        return
      }
      const monthStart = dateMoment.clone().startOf('month')
      setCurrentMonth(monthStart)
    }
  }

  const handleToggleInclude = (index) => {
    // Don't change ticks while availability is being checked
    if (isCheckingAvailability) {
      return
    }
    setSelectedDatesList((prev) => toggleDateIncludedInList(prev, index))
  }

  const handleBackToAllDates = () => {
    editingDateIndexRef.current = null
    setEditingDateIndex(null)
    setEditingBaseDate(null)
    setEditingduration(null)
    skipNextLoadRef.current = false // Reset flag when going back
    setSelectedDate(selectedDatesList[0]?.date || moment().format('YYYY-MM-DD'))
  }

  const handleResetDates = () => {
    editingDateIndexRef.current = null
    setSelectedDatesList([])
    setShowSelectedDates(false)
    setSelectedSlot(null)
    setEditingDateIndex(null)
    setEditingBaseDate(null)
    setEditingduration(null)
    skipNextLoadRef.current = false // Reset flag when resetting
    const today = moment().format('YYYY-MM-DD')
    setSelectedDate(today)
    setCurrentMonth(moment().startOf('month'))
  }

  const getPageTitle = () => {
    switch (schedulingType) {
      case 'remaining':
        return 'Schedule More Sessions'
      case 'schedule-all':
        // If only one session remaining, show singular title
        const totalSessions = getTotalSessions()
        return totalSessions === 1
          ? 'Schedule More Session'
          : 'Schedule Multiple Sessions'
      default:
        return 'Schedule Session'
    }
  }

  const isSubmitting = isScheduling || isConfirmingWeekly

  const getButtonText = () => {
    if (isSubmitting) {
      return 'SCHEDULING...'
    }
    if (isCheckingAvailability && schedulingType === 'schedule-all') {
      return 'CHECKING AVAILABILITY...'
    }
    switch (schedulingType) {
      case 'remaining':
        return 'SCHEDULE'
      case 'schedule-all':
        const totalSessions = getTotalSessions()
        if (totalSessions !== 1 && showSelectedDates && schedulableCount > 0) {
          return `SCHEDULE ${schedulableCount} ${
            schedulableCount === 1 ? 'SESSION' : 'SESSIONS'
          }`
        }
        return totalSessions === 1 ? 'SCHEDULE' : 'Schedule Multiple Sessions'
      default:
        return 'SCHEDULE'
    }
  }

  const hasBlockingSlots = useMemo(() => {
    if (schedulingType !== 'schedule-all' || !showSelectedDates) {
      return false
    }
    return hasBlockingUnavailableSlots(selectedDatesList)
  }, [schedulingType, showSelectedDates, selectedDatesList])

  const schedulableCount = useMemo(
    () => getSchedulableDatesFromList(selectedDatesList || []).length,
    [selectedDatesList],
  )

  const unavailableSlotsTooltip = useMemo(
    () => getUnavailableSlotsTooltip(selectedDatesList),
    [selectedDatesList],
  )

  // First session is compulsory while nothing is booked yet
  const isFirstSessionBlocked = useMemo(() => {
    if (schedulingType !== 'schedule-all' || !showSelectedDates) {
      return false
    }
    return (
      isFirstSessionScheduling(coachDetails) &&
      isFirstSessionMissing(selectedDatesList)
    )
  }, [schedulingType, showSelectedDates, coachDetails, selectedDatesList])

  const isButtonDisabled = useMemo(() => {
    if (isSubmitting) {
      return true
    }
    if (isCheckingAvailability) {
      return true
    }
    if (schedulingType === 'schedule-all') {
      if (isFirstSessionBlocked) {
        return true
      }
      // Enabled as long as at least one date is error-free; the submit
      // handlers send only those dates.
      return !selectedDatesList || schedulableCount === 0
    }
    return !selectedDate || !selectedSlot
  }, [
    isSubmitting,
    isCheckingAvailability,
    schedulingType,
    selectedDatesList,
    schedulableCount,
    selectedDate,
    selectedSlot,
    isFirstSessionBlocked,
  ])

  const duration = hasGrowthInRemaining ? firstSlotDuration : otherSlotsDuration

  return (
    <div className='session-scheduling-calendar'>
      <SchedulingHeader title={getPageTitle()} onBack={onBack} />

      <SchedulingMetaPanel
        coachClientName={coachClientName}
        duration={duration}
        totalSessions={getTotalSessions()}
        firstSlotDuration={firstSlotDuration}
        otherSlotsDuration={otherSlotsDuration}
        hasGrowthInRemaining={hasGrowthInRemaining}
        schedulingType={schedulingType}
        sowType={sowType}
      />

      <div className='session-scheduling-calendar__content mt-30px'>
        <div
          className='border border-dark d-flex flex-column flex-md-row overflow-hidden'
          style={{ minHeight: 490, height: 490 }}>
          <div
            className='flex-fill p-3 border border-dark border-top-0 border-bottom-0 border-left-0 d-flex flex-column'
            style={{ minHeight: 0 }}>
            <ScheduleDatePicker
              currentMonth={currentMonth}
              selectedDate={
                schedulingType === 'schedule-all' &&
                showSelectedDates &&
                editingDateIndex !== null
                  ? selectedDate
                  : schedulingType === 'schedule-all' && showSelectedDates
                  ? null
                  : selectedDate
              }
              selectedDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? editingDateIndex === null
                    ? selectedDatesList.map((item) => item.date)
                    : []
                  : []
              }
              onMonthChange={handleMonthChange}
              onDateSelect={handleDateSelect}
              availableDays={availableDays}
              multiple={
                schedulingType === 'schedule-all' &&
                showSelectedDates &&
                editingDateIndex === null
              }
              disablePastDates={true}
              isLoading={isLoadingSlots}
              disableDatesWithoutSlots={true}
              unavailableDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? selectedDatesList
                      .filter(
                        (item) =>
                          item.isSlotAvailable === false &&
                          !isSessionRestrictionSlot(item),
                      )
                      .map((item) => item.date)
                  : []
              }
              restrictionDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? selectedDatesList
                      .filter(
                        (item) =>
                          item.isSlotAvailable === false &&
                          isSessionRestrictionSlot(item),
                      )
                      .map((item) => item.date)
                  : []
              }
            />
          </div>
          {/* Flex basis 0 so long error text can't widen this panel; it takes the space left by the calendar */}
          <div
            className='px-4 py-3 d-flex flex-column overflow-hidden'
            style={{ minHeight: 0, minWidth: 0, flex: '1 1 0%' }}>
            {schedulingType === 'schedule-all' &&
            showSelectedDates &&
            editingDateIndex === null ? (
              <SelectedDatesList
                selectedDates={selectedDatesList}
                timeFormat={timeFormat}
                timezoneOffsetMinutes={timezoneOffsetMinutes}
                timezone={timezone}
                onDateClick={handleDateClickFromList}
                onReset={handleResetDates}
                onToggleInclude={handleToggleInclude}
                isCheckingAvailability={isCheckingAvailability}
                showBackButton={false}
                onBackClick={handleBackToAllDates}
              />
            ) : (
              <ScheduleTimeSlots
                slots={timeSlots}
                selectedSlot={selectedSlot}
                onSlotSelect={handleSlotClickInternal}
                initialFormat={timeFormat}
                isLoading={isLoadingSlots}
                timezoneOffsetMinutes={timezoneOffsetMinutes}
                timezone={timezone}
                showBackButton={
                  schedulingType === 'schedule-all' &&
                  showSelectedDates &&
                  editingDateIndex !== null
                }
                onBackClick={handleBackToAllDates}
              />
            )}
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-3'>
        {isButtonDisabled &&
        (hasBlockingSlots || isFirstSessionBlocked) &&
        !isSubmitting &&
        !isCheckingAvailability ? (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='schedule-all-tooltip'>
                {isFirstSessionBlocked
                  ? FIRST_SESSION_REQUIRED_MESSAGE
                  : unavailableSlotsTooltip ||
                    'Coach is not available, please change it first'}
              </Tooltip>
            }>
            <span className='d-inline-block'>
              <Button
                variant='gray'
                size='md'
                className='line-height-normal'
                onClick={handleScheduleWithErrorHandling}
                disabled={true}>
                {getButtonText()}
              </Button>
            </span>
          </OverlayTrigger>
        ) : (
          <Button
            variant={isButtonDisabled ? 'gray' : 'saffron'}
            size='md'
            className='line-height-normal'
            onClick={handleScheduleWithErrorHandling}
            disabled={isButtonDisabled}>
            {getButtonText()}
          </Button>
        )}
      </div>

      <ScheduleSettingsModal
        show={showScheduleSettingsModal}
        handleClose={() => setShowScheduleSettingsModal(false)}
        onConfirm={handleRecurringPatternConfirm}
        selectedDate={selectedDate}
        coachDetails={coachDetails}
      />
      <WeeklySessionWarningModal
        isOpen={weeklyModal.isOpen}
        message={weeklyModal.message}
        onConfirm={handleWeeklyConfirm}
        onCancel={closeWeeklyModal}
        loading={isConfirmingWeekly}
      />
    </div>
  )
}

export default SessionSchedulingCalendar
